import { Box, Typography } from '@mui/material';
import { useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import type { AddAssetArgs } from '../VNFixture/VNFixture';
import VNFixture from '../VNFixture/VNFixture';
import { VNLibraryType } from '../VNLibrary/VNLibrary';

const ImageUploader = ({
  field = null,
  title = null,
  onItemSelected,
  watchField = 'tmp.itemLookup',
  readOnly = false,
  required = false,
  mode = 'library',
  previewSrc = null,
  accept = 'image/png,image/jpeg,.jpg,.jpeg,.png',
  onFileSelected,
  onBeforeBrowse,
  onRemove,
  showCheckerboard = true,
}: {
  field?: string | null;
  title: string;
  onItemSelected?: (value: AddAssetArgs) => void | null;
  watchField?: string;
  readOnly?: boolean;
  required?: boolean;
  mode?: 'library' | 'file';
  previewSrc?: string | null;
  accept?: string;
  onFileSelected?: (file: File) => void;
  onBeforeBrowse?: () => boolean | Promise<boolean>;
  onRemove?: () => void | Promise<void>;
  showCheckerboard?: boolean;
}) => {
  const { getValues, watch, getFieldState, formState } = useFormContext();
  const { error } = field ? getFieldState(field, formState) : { error: undefined };
  const images = watch(watchField);
  const isFileUpload = mode === 'file';
  const fileInput = useRef<HTMLInputElement | null>(null);
  const config: {
    variant: 'square' | 'landscape' | 'poster' | 'banner' | 'logo';
    dimensions: string;
  } = useMemo(() => {
    switch (title) {
      case 'Poster':
        return {
          variant: 'poster',
          dimensions: '1080 x 1920',
        };
      case 'Banner':
        return {
          variant: 'banner',
          dimensions: '2400 x 600',
        };
      case 'Logo':
        return {
          variant: 'logo',
          dimensions: '330 x 330',
        };
      case 'Thumbnail':
        return {
          variant: 'landscape',
          dimensions: '1280 x 720',
        };
      default:
        return {
          variant: 'landscape',
          dimensions: '1920 x 1080',
        };
    }
  }, [title]);

  const openFilePicker = async () => {
    if (onBeforeBrowse) {
      const allowed = await onBeforeBrowse();
      if (!allowed) {
        return;
      }
    }
    fileInput.current?.click();
  };

  const handleFileChange = (fileList: FileList | null) => {
    const file = fileList?.item(0);
    if (!file) {
      return;
    }
    onFileSelected?.(file);
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  return (
    <Box p={1}>
      <Box sx={{ display: 'inline-block', width: '250px' }}>
        {isFileUpload && (
          <input
            ref={fileInput}
            accept={accept}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => handleFileChange(e.target.files)}
          />
        )}
        <VNFixture
          variant={config.variant}
          field={isFileUpload ? null : field}
          type={VNLibraryType.Image}
          item={isFileUpload || !field ? undefined : images?.[getValues(field)]}
          onItemSelected={onItemSelected}
          isReadOnly={readOnly}
          previewSrc={isFileUpload ? previewSrc : null}
          onAddClick={isFileUpload ? openFilePicker : undefined}
          onRemove={isFileUpload ? onRemove : undefined}
          showCheckerboard={showCheckerboard}
        />
      </Box>
      <Typography variant="h6">
        {title}
        {required ? ' *' : ''}
      </Typography>
      <span style={{ color: '#4B5563 !important', fontSize: '12px !important' }}>{config.dimensions} | .png, .jpg, .jpeg</span>
      {error?.message && (
        <Typography variant="caption" color="error" display="block">
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUploader;
